import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LedgerRow, ReportData } from "../types";
import { fmt } from "./format";

// Exports of the transaction ledger. Both read the normalized ReportData —
// the same object the screen renders — and never recompute a figure.

const fileStem = (report: ReportData) =>
  report.period.mode === "month"
    ? `Balance_Sheet_${report.period.year}-${String(report.period.month).padStart(2, "0")}`
    : `Balance_Sheet_${report.period.startDate || "all"}_to_${report.period.endDate || "date"}`;

export const exportLedgerExcel = (report: ReportData, rows: LedgerRow[]) => {
  const data: Record<string, string | number>[] = [];
  if (report.period.startDate) {
    data.push({ Date: report.period.startDate, "Category / Source": "", Description: "Carry Forward", Expense: "", Income: "", Balance: report.carryForward });
  }
  rows.forEach((r) =>
    data.push({
      Date: r.date,
      "Category / Source": r.label,
      Description: r.description,
      Expense: r.expenseAmount ?? "",
      Income: r.incomeAmount ?? "",
      Balance: r.runningBalance,
    })
  );
  data.push({ Date: "", "Category / Source": "", Description: "TOTAL", Expense: report.expenseTotal, Income: report.incomeTotal, Balance: report.balanceOnHand });

  const sheet = XLSX.utils.json_to_sheet(data);
  sheet["!cols"] = [{ wch: 12 }, { wch: 20 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Balance Sheet");
  XLSX.writeFile(book, `${fileStem(report)}.xlsx`);
};

/** Loads an image as a data URL for jsPDF; resolves null if it can't be loaded. */
export const loadImageAsDataUrl = (url: string): Promise<string | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

export const exportLedgerPdf = async (report: ReportData, rows: LedgerRow[], logoUrl: string) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;
  let y = 20;

  const logo = await loadImageAsDataUrl(logoUrl);
  if (logo) {
    try {
      const props = doc.getImageProperties(logo);
      doc.addImage(logo, "PNG", marginX, y - 9, (props.width / props.height) * 12, 12);
    } catch {
      // a broken logo never blocks the export
    }
  }

  doc.setFontSize(16);
  doc.setTextColor(33, 33, 33);
  doc.text(`Balance Sheet — ${report.project.name}`, pageWidth / 2, y, { align: "center" });
  y += 6;
  doc.setFontSize(9.5);
  doc.setTextColor(130, 130, 130);
  doc.text(report.period.label, pageWidth / 2, y, { align: "center" });
  y += 10;

  // Summary in the same order as the on-screen cards.
  const summary: [string, string, [number, number, number]][] = [
    ["CARRY FORWARD", fmt(report.carryForward), [55, 71, 79]],
    ["TOTAL INCOME", `+ ${fmt(report.incomeTotal)}`, [46, 125, 50]],
    ["TOTAL EXPENSES", `- ${fmt(report.expenseTotal)}`, [198, 40, 40]],
    ["BALANCE ON HAND", fmt(report.balanceOnHand), report.balanceOnHand < 0 ? [198, 40, 40] : [46, 125, 50]],
  ];
  const colWidth = (pageWidth - marginX * 2) / 4;
  summary.forEach(([label, value, rgb], i) => {
    const cx = marginX + colWidth * i + colWidth / 2;
    doc.setFontSize(8.5);
    doc.setTextColor(140, 140, 140);
    doc.text(label, cx, y, { align: "center" });
    doc.setFontSize(13);
    doc.setTextColor(...rgb);
    doc.text(value, cx, y + 6, { align: "center" });
  });
  y += 12;

  const body: (string | { content: string; styles: object })[][] = [];
  if (report.period.startDate) {
    body.push([report.period.startDate, "", { content: "Carry Forward", styles: { fontStyle: "italic" } }, "", "", fmt(report.carryForward)]);
  }
  rows.forEach((r) =>
    body.push([
      r.date,
      r.label,
      r.description || "—",
      r.expenseAmount != null ? fmt(r.expenseAmount) : "",
      r.incomeAmount != null ? fmt(r.incomeAmount) : "",
      fmt(r.runningBalance),
    ])
  );

  autoTable(doc, {
    startY: y,
    head: [["Date", "Category / Source", "Description", "Expense", "Income", "Balance"]],
    body,
    foot: [["", "", "Total", fmt(report.expenseTotal), fmt(report.incomeTotal), fmt(report.balanceOnHand)]],
    styles: { fontSize: 8.5, cellPadding: 2.2 },
    headStyles: { fillColor: [63, 81, 181], textColor: 255, fontSize: 9.5 },
    footStyles: { fillColor: [245, 246, 248], textColor: 33, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      3: { halign: "right", textColor: [198, 40, 40] },
      4: { halign: "right", textColor: [46, 125, 50] },
      5: { halign: "right", fontStyle: "bold" },
    },
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${doc.getCurrentPageInfo().pageNumber}`,
        pageWidth - marginX,
        doc.internal.pageSize.getHeight() - 8,
        { align: "right" }
      );
    },
  });

  doc.save(`${fileStem(report)}.pdf`);
};
